/** Gem economy + card prices — docs/03-upgrades-economy.md */
export const ECONOMY = {
  gems: {
    /** Magnet attraction radius (multiplied by stat card) */
    magnetRadius: 170,
    /** Pull speed range: far gems drift in slowly, near gems snap (no orbiting) */
    pullMin: 140,
    pullMax: 860,
    /** Collection radius */
    collectRadius: 36,
    /** Scatter speed range */
    scatterSpeed: [120, 340] as const,
    friction: 3.2,
    /** Yellow gems evaporate after this many seconds (red gems never do) */
    ttl: 12,
    /** Blink warning window before a gem evaporates */
    blinkTime: 5,
    /**
     * Fewer, bigger pieces (wave 4 loot audit, docs/03): a burst worth V
     * becomes ceil(V / pieceValue) pieces; value is never rounded away.
     */
    pieceValue: { yellow: 5, red: 2 },
    /** Live pieces per kind; past this a new piece merges into the nearest */
    maxLive: 14,
    /** Sprite scale by piece value — bigger gem = bigger number */
    valueScale: [
      { min: 1, yellow: 0.72, red: 1.25 },
      { min: 3, yellow: 0.9, red: 1.4 },
      { min: 5, yellow: 1.05, red: 1.55 },
      { min: 9, yellow: 1.2, red: 1.7 },
      { min: 15, yellow: 1.35, red: 1.85 },
    ],
  },

  /** Loot meter: 1 yellow of value per this much damage dealt; minion kills
   * feed the same meter (docs/12 rebalance, wave 4 consolidation). 95 → 102
   * in wave 4: keeping the best weapons (no silent overwrite) lands ~7% more
   * AoE damage per room; this keeps the yellow per room where it was. */
  dripPerDamage: 102,
  /** The meter pays out one piece of this value (no more 1-gem confetti) */
  dripPieceValue: 5,
  /** Hit-drip gems scatter wider than kill bursts */
  dripScatterMul: 1.7,

  /** Yellow gems scattered on boss death: base + roomNumber * perArena */
  bossGemBase: 10,
  bossGemPerArena: 3,
  droneGems: 1,
  /** Small yellow burst on every boss phase change (mid-fight trickle) */
  phaseGemBurst: 3,
  /** Red gems on boss kill: redBase + floor(level / redPerLevels) — red was
   * too scarce, so a flat floor plus a level curve (docs/12 rebalance).
   * Boss level now follows run depth (~0.85/room), hence the wider step. */
  redBase: 2,
  redPerLevels: 5,
  /** Chance an elite/heavy minion drops a red core */
  redFromEliteChance: 0.18,
  /** Yellow boss payout jitter range (±) */
  bossGemJitter: 0.3,
  /** Boss bonus loot roll — cumulative chances; remainder drops NOTHING */
  bossLoot: {
    weapon: 0.55,
    shield: 0.15,
    repair: 0.1,
  },

  /** Yellow prices (common/uncommon/rare cards; epic+ cost red CORES) */
  cardPrice: {
    common: 15,
    uncommon: 26,
    rare: 42,
  },
  /** Yellow prices inflate per cleared room (Brotato-style wave scaling) */
  priceRoomScale: 0.12,
  /** Red CORE prices by rarity (docs/12 H5); the gun-tier card stays cheap */
  redPrice: {
    tier: 2,
    epic: 3,
    legendary: 5,
    mythic: 8,
  },
  /** 6-tier rarity weights (docs/12 H5) */
  rarityWeight: {
    common: 0.4,
    uncommon: 0.27,
    rare: 0.17,
    epic: 0.1,
    legendary: 0.05,
    mythic: 0.01,
  },
  /**
   * Weapon BAG (spares, NOT counting the sockets — docs/09 wave 4): 2 slots
   * at start, each Cargo Bay buy +1, at most `total`.
   */
  inventory: {
    total: 6,
    start: 2,
  },
  /**
   * Red price of socket 2 / 3, bought on the hangar padlock (wave 4; the
   * Weapon Rack card left the offer pool). Tuned in the headless sim so the
   * sockets open on the same room curve as the old random Rack offers.
   */
  socketPrice: [16, 36] as const,
  /** Shop heal: +25 HP for red CORES; price climbs per purchase, resets each break */
  heal: {
    basePrice: 2,
    priceStep: 2,
  },
  /** Shop armor: +1 armor unit for YELLOW gems — pricey on purpose, climbs
   * per purchase, resets each break; a card cuts it (docs/12 O) */
  armorBuy: {
    basePrice: 14,
    priceStep: 8,
    /** Floor after the Plating Deal card cut */
    minPrice: 2,
  },
  /** Cards per shop offer (locked carry-overs count toward it) */
  offerSize: 3,
  rerollBase: 10,
  /** Floor after the Reroll Voucher cut */
  rerollMin: 1,
  rerollStep: 6,
  /** Reroll opening price also climbs with the room number */
  rerollRoomStep: 2,
  /** Reroll Voucher card: price multiplier by stack count (docs/12 I6) */
  rerollDiscountByStack: [1, 0.9, 0.75, 0.5] as const,

  /** Death tax + selective burning (docs/12 C4) */
  deathTax: {
    /** Mandatory burns: collection below tier0 → 1, below tier1 → 2, else 3 */
    sizeTiers: [10, 20] as const,
    /** Card candidates offered to choose from */
    candidates: 5,
    /** Tax level that adds a WEAPON to the candidates (docs/12 F3) */
    weaponTaxMin: 2,
    /** Optional burns past the tax grant armor units, by card rarity */
    burnArmor: {
      common: 1, uncommon: 1, rare: 1, epic: 2, legendary: 2, mythic: 3,
    } as Record<string, number>,
    weaponBurnArmor: 1,
  },
} as const;
